// src/components/ProductCard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatPrice } from '../services/shopApi';
import './ProductCard.css';
import { ShippingBadge } from './ShippingBadge';

// Country flag emoji from ISO-2 code
// Regional indicator letters start at U+1F1E6 ('A')
const countryFlag = (code) => {
  if (!code) return '🌐';
  const upper = code.toUpperCase();
  // Both 'GL' (schema value) and 'GLOBAL' (legacy value) mean worldwide
  if (upper === 'GL' || upper === 'GLOBAL') return '🌐';
  if (!/^[A-Z]{2}$/.test(upper)) return '🌐';
  return [...upper].map(c =>
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
  ).join('');
};

// Valid ISO-2 country codes — used to avoid showing flags for garbage data
// Includes both 'GL' (schema) and 'GLOBAL' (legacy) as valid markers.
const VALID_COUNTRY = new Set([
  'US','VN','SG','JP','AU','GB','CA','DE','FR','KR','TH','MY','ID','PH',
  'AE','SA','CN','NZ','MX','BR','IN','IT','ES','NL','SE','NO','DK','FI',
  'PL','AT','CH','BE','PT','IE','HK','TW','TR','ZA','NG','EG','AR','CL',
  'CO','PE','VE','UA','RU','GL','GLOBAL',
]);

export default function ProductCard({ product, onAdd, adding }) {
  // Track if the <img> failed to load so we can show the placeholder via state,
  // not DOM hacks. More reliable than e.currentTarget.nextSibling.
  const [imgFailed, setImgFailed] = useState(false);

  // ── Image ───────────────────────────────────────────────────────────────────
  // primaryImage can be: { url, alt } | plain string | null
  const rawImg = product.primaryImage;
  const img    = rawImg?.url || (typeof rawImg === 'string' ? rawImg : null)
              || product.images?.[0]?.url || null;

  // ── Pricing — always prefer market-resolved fields from shopController ──────
  // product.currentPrice / product.currency come from formatProductForShop()
  // Fall back to product.pricing.retail / product.pricing.currency for legacy
  const price    = product.currentPrice    ?? product.pricing?.retail    ?? 0;
  const origPrice = product.originalPrice  ?? product.pricing?.retail    ?? 0;
  const currency  = product.currency       || product.pricing?.currency  || 'USD';
  const disc      = product.discountPercent > 0 ? product.discountPercent : 0;

  // ── Market info ─────────────────────────────────────────────────────────────
  const marketInfo = product.marketInfo || null;
  // Only show a flag/country if it's a real known ISO-2 code
  const rawShipFrom = product.shipping?.shipFromCountry || marketInfo?.country || null;
  const shipFromUpper = rawShipFrom ? rawShipFrom.toUpperCase() : null;
  // Treat GL/GLOBAL as NOT a ship-from country (they're global markers, not a country)
  const shipFrom    = shipFromUpper && VALID_COUNTRY.has(shipFromUpper)
                   && shipFromUpper !== 'GL' && shipFromUpper !== 'GLOBAL'
    ? shipFromUpper : null;
  const isLocalMarket = marketInfo?.isLocal || false;
  const isGlobal      = product.listingType === 'global'
                     || !product.targetMarkets?.length
                     || marketInfo?.country === 'GLOBAL'
                     || marketInfo?.country === 'GL'
                     || product.country === 'GL'
                     || product.country === 'GLOBAL'
                     || product.isGlobalListing === true;

  // ── Other fields ────────────────────────────────────────────────────────────
  const rating  = product.stats?.averageRating || 0;
  const reviews = product.stats?.totalReviews  || 0;
  const id      = product.productId || product._id;
  const inStock      = product.inStock !== false; // default true if not set
  const unavailable  = product.unavailableInVisitorCountry === true;

  return (
    <div className={`product-card ${!inStock ? 'out-of-stock' : ''} ${unavailable ? 'unavailable-region' : ''}`}>
      {/* ── Badges ── */}
      {product.freeShipping && (
        <span className="badge badge-green pc-badge-free">Free Ship</span>
      )}
      {disc > 0 && (
        <span className="badge badge-red pc-badge-sale">−{disc}%</span>
      )}

      {/* ── Image ── */}
      <Link to={`/products/${id}`} className="pc-img-wrap" tabIndex={-1}>
        {img && !imgFailed ? (
          <img
            src={img}
            alt={product.name}
            className="pc-img"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="pc-img-placeholder">📦</div>
        )}
        {!inStock && <div className="pc-sold-out">Out of Stock</div>}
      </Link>

      {/* ── Body ── */}
      <div className="pc-body">
        <div className="pc-brand">
          {product.businessName || product.brand || 'JepTags'}
        </div>

        <Link to={`/products/${id}`} className="pc-name">{product.name}</Link>

        {rating > 0 && (
          <div className="pc-rating">
            <span className="pc-stars">
              {'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}
            </span>
            {reviews > 0 && <span className="pc-reviews">({reviews})</span>}
          </div>
        )}

        {/* ── Price — uses market-resolved currency ── */}
        <div className="pc-price-row">
          <span className="pc-price">{formatPrice(price, currency)}</span>
          {disc > 0 && origPrice > price && (
            <>
              <span className="pc-orig">{formatPrice(origPrice, currency)}</span>
              <span className="pc-pct">−{disc}%</span>
            </>
          )}
        </div>

        {/* ── Shipping ── */}
        {product.freeShipping ? (
          <div className="pc-shipping">✓ Free Delivery</div>
        ) : product.shipping?.cost > 0 ? (
          <div className="pc-shipping-cost">
            +{formatPrice(product.shipping.cost, currency)} shipping
          </div>
        ) : null}

        {/* ── Ships-to availability badge ── */}
        <ShippingBadge product={product} size="sm" />

        {/* ── Market / origin badge ── */}
        {(shipFrom || isGlobal) && (
          <div className={`pc-market-badge ${isLocalMarket ? 'is-local' : isGlobal ? 'is-global' : 'is-intl'}`}>
            <span className="pc-market-flag">
              {isLocalMarket ? countryFlag(shipFrom) : isGlobal ? '🌐' : countryFlag(shipFrom)}
            </span>
            <span className="pc-market-label">
              {isLocalMarket
                ? `Local · ${shipFrom}`
                : isGlobal
                  ? 'International'
                  : `From ${shipFrom}`}
            </span>
            {product.shipping?.minDays && product.shipping?.maxDays && (
              <span className="pc-market-days">
                · {product.shipping.minDays}–{product.shipping.maxDays}d
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="pc-footer">
        <button
          className={`pc-add ${adding ? 'added' : ''}`}
          onClick={e => { e.preventDefault(); onAdd && onAdd(product); }}
          disabled={adding || !inStock || unavailable}
          aria-label={`Add ${product.name} to cart`}
        >
          {adding       ? '✓ Added to Cart'
           : !inStock    ? 'Out of Stock'
           : unavailable ? 'Unavailable in your region'
           : '+ Add to Cart'}
        </button>
      </div>
    </div>
  );
}